import type { AnyAction, Store, StoreEnhancer } from 'redux';
import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import type { ThunkDispatch } from 'redux-thunk';
import rootReducer, { type RootReduxState } from 'app/shared/flux/reducers/index';

declare const __DEV__: boolean;

/**
 * Properly typed dispatch that supports both regular actions and thunk actions
 */
export type AppThunkDispatch = ThunkDispatch<RootReduxState, unknown, AnyAction>;

/**
 * Redux store with properly typed dispatch method
 * This ensures window.store.dispatch() accepts both actions and thunks
 */
export type AppStore = Omit<Store<RootReduxState>, 'dispatch'> & {
  dispatch: AppThunkDispatch;
};

let store: AppStore;

export default function configureStore(initialState = {}): AppStore {
  if (__DEV__) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
    const createLogger = require('redux-logger').createLogger;
    const logger = createLogger({
      duration: true,
      collapsed: true,
    });
    const devToolsEnhancer: StoreEnhancer =
      typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION__
        ? window.__REDUX_DEVTOOLS_EXTENSION__()
        : (((f: unknown) => f) as unknown as StoreEnhancer);

    const finalCreateStore = compose(
      applyMiddleware(thunk),
      typeof window === 'object' ? applyMiddleware(logger) : (((f: unknown) => f) as unknown as StoreEnhancer),
      devToolsEnhancer,
    )(createStore);

    store = finalCreateStore(rootReducer(), initialState) as AppStore;
  } else {
    const finalCreateStore = compose(applyMiddleware(thunk))(createStore);
    store = finalCreateStore(rootReducer(), initialState) as AppStore;
  }

  return store;
}

// Export as a named export for direct import and
// reading state from various non-React modules
export { store };
